import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import AddCustomerModal from './AddCustomerModal';

const emptyBike = () => ({ color_id: '', chassis_number: '', engine_number: '', engine_error: '', chassis_error: '' });

const AddPurchaseForm = ({ onComplete }) => {
    const [purchaseType, setPurchaseType] = useState('Supplier');
    const [supplierResults, setSupplierResults] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [colors, setColors] = useState([]);
    const [versionsList, setVersionsList] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);

    const [lotDetails, setLotDetails] = useState({ model_name: '', version: '', cc: '', unit_price: '' });
    const [bikes, setBikes] = useState([emptyBike()]);
    const [payments, setPayments] = useState([{ method: 'Cash', account_id: '', amount: '' }]);

    // Ref array for keyboard navigation (ArrowUp / ArrowDown / Enter)
    const formElementsRef = useRef([]);

    useEffect(() => {
        loadInitialData();
    }, []);

    // Debounced supplier search (calls searchSuppliers API)
    useEffect(() => {
        const term = searchTerm.trim();
        if (!term) {
            setSupplierResults([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const res = await API.get('/v1/suppliers/search', { params: { query: term } });
                if (!cancelled) setSupplierResults(res.data.data || []);
            } catch (err) {
                if (!cancelled) setSupplierResults([]);
                console.error('Supplier search error:', err);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchTerm]);

    const loadInitialData = async () => {
        try {
            const [resAcc, resColors, resVersions] = await Promise.all([
                API.get('/v1/purchase/payment-accounts'),
                API.get('/v1/colors'),
                API.get('/v1/versions')
            ]);
            setPaymentAccounts(resAcc.data.data || resAcc.data || []);
            setColors(resColors.data.data || resColors.data || []);
            setVersionsList(resVersions.data.data || resVersions.data || []);
        } catch (err) {
            console.error('Initialization Error:', err);
        }
    };

    // When a version is picked, auto-fill CC if the version record carries it
    const handleVersionChange = (value) => {
        const rec = versionsList.find(v => v.version === value && (!lotDetails.model_name || v.model_name === lotDetails.model_name));
        setLotDetails(prev => ({ ...prev, version: value, cc: rec && rec.cc != null ? String(rec.cc) : prev.cc }));
    };

    const totalAmount = Math.round((parseFloat(lotDetails.unit_price) || 0) * bikes.length);

    // Keyboard navigation
    const handleKeyDown = (e, index) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            e.preventDefault();
            const nextElement = formElementsRef.current[index + 1];
            if (nextElement) nextElement.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevElement = formElementsRef.current[index - 1];
            if (prevElement) prevElement.focus();
        }
    };

    const updateBike = (index, changes) => {
        setBikes(prev => prev.map((b, i) => (i === index ? { ...b, ...changes } : b)));
    };

    const handleBikeDetailChange = (index, field, value) => {
        const changes = { [field]: value };
        // Clear old duplicate warning when the user edits the field
        if (field === 'engine_number') changes.engine_error = '';
        if (field === 'chassis_number') changes.chassis_error = '';
        updateBike(index, changes);
    };

    // Duplicate check runs when the user leaves the field (not on every keystroke)
    const checkDuplicate = async (index, field) => {
        const value = (bikes[index][field] || '').trim();
        const errorKey = field === 'engine_number' ? 'engine_error' : 'chassis_error';
        const label = field === 'engine_number' ? 'Engine number' : 'Chassis number';
        if (value.length < 3) return;

        // Duplicate inside the same form
        const repeatedInForm = bikes.some((b, i) => i !== index && (b[field] || '').trim().toLowerCase() === value.toLowerCase());
        if (repeatedInForm) {
            updateBike(index, { [errorKey]: `${label} is entered more than once in this form.` });
            return;
        }

        try {
            const res = await API.post('/v1/purchase/check-duplicate-stock', { [field]: value });
            if (res.data && res.data.exists) {
                updateBike(index, { [errorKey]: `${label} already exists in available stock.` });
            }
        } catch (err) {
            console.error('Duplicate check error:', err);
        }
    };

    const addBikeRow = () => {
        if (purchaseType !== 'Supplier') {
            return alert('Market and Buy-Back purchases allow only one bike.');
        }
        setBikes([...bikes, emptyBike()]);
    };

    const removeBikeRow = (index) => index !== 0 && setBikes(bikes.filter((_, i) => i !== index));
    const addPaymentRow = () => setPayments([...payments, { method: 'Cash', account_id: '', amount: '' }]);
    const removePaymentRow = (index) => setPayments(payments.filter((_, i) => i !== index));

    const updatePayment = (index, changes) => {
        setPayments(prev => prev.map((p, i) => (i === index ? { ...p, ...changes } : p)));
    };

    const submitForm = async () => {
        if (purchaseType === 'Supplier' && !selectedSupplier) return alert('Please select a supplier.');
        if (purchaseType === 'Market' && !selectedCustomer) return alert('Please select the seller.');
        if (purchaseType === 'Buy_Back' && !selectedCustomer) return alert('Please select the buy-back customer.');

        if (!lotDetails.model_name || !lotDetails.version || !lotDetails.cc || !lotDetails.unit_price) {
            return alert('Please fill in Make, Version, CC and Unit Price.');
        }

        for (let i = 0; i < bikes.length; i++) {
            const b = bikes[i];
            if (!b.color_id || !b.engine_number.trim() || !b.chassis_number.trim()) {
                return alert(`Bike #${i + 1}: Color, Engine number and Chassis number are required.`);
            }
            if (b.engine_error || b.chassis_error) {
                return alert(`Bike #${i + 1}: ${b.engine_error || b.chassis_error}`);
            }
        }

        for (let i = 0; i < payments.length; i++) {
            const p = payments[i];
            if (parseFloat(p.amount) > 0 && !p.account_id) {
                return alert(`Payment #${i + 1}: Please select a ledger account.`);
            }
        }

        setLoading(true);
        try {
            const payload = {
                purchase_type: purchaseType,
                supplier_id: selectedSupplier?.id || null,
                customer_id: selectedCustomer?.id || null,
                invoice_number: invoiceNumber,
                remarks,
                model_name: lotDetails.model_name,
                version: lotDetails.version,
                cc: lotDetails.cc,
                unit_price: lotDetails.unit_price,
                payments,
                bikes: bikes.map(b => ({
                    color_id: b.color_id,
                    engine_number: b.engine_number.trim(),
                    chassis_number: b.chassis_number.trim()
                }))
            };

            const res = await API.post('/v1/purchase/comprehensive-process', payload);
            if (res.data.success) {
                alert('Purchase saved successfully.');
                if (onComplete) onComplete();
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Server processing error.');
        } finally {
            setLoading(false);
        }
    };

    const uniqueModels = [...new Set(versionsList.map(v => v.model_name))].filter(Boolean);
    const filteredVersions = versionsList.filter(v => {
        if (!lotDetails.model_name) return true;
        return v.model_name === lotDetails.model_name;
    });

    // Phone shown in the reference box instead of the ID
    const partyPhone = purchaseType === 'Supplier' ? selectedSupplier?.phone : selectedCustomer?.phone;

    let elIdx = 0;

    return (
        <div style={containerStyle}>
            {/* HEADER WITH TABS */}
            <div style={headerCard}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>
                        {purchaseType === 'Supplier' ? 'New Purchase (Bulk Purchase)' : 'Purchase (Single Purchase)'}
                    </h2>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>Super Star ERP Module - Inventory & Accounts</span>
                </div>
                <div style={tabContainer}>
                    {['Supplier', 'Market', 'Buy_Back'].map(t => (
                        <button key={t} style={purchaseType === t ? activeTab : tabStyle} onClick={() => {
                            setPurchaseType(t);
                            setSelectedCustomer(null);
                            setSelectedSupplier(null);
                            setSearchTerm('');
                            setCustomerSearch('');
                            if (t !== 'Supplier') setBikes([emptyBike()]);
                        }}>
                            {t.replace('_', ' ')}
                        </button>
                    ))}
                </div>
            </div>

            {/* TOP META (PV # & Date) */}
            <div style={metaGrid}>
                <div style={metaFieldBox}>
                    <span style={metaLabel}>PV #</span>
                    <input style={disabledInput} value="Auto Generated" readOnly />
                </div>
                <div style={metaFieldBox}>
                    <span style={metaLabel}>DATE</span>
                    <input
                        style={inputStyle}
                        defaultValue={new Date().toISOString().split('T')[0]}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    />
                </div>
            </div>

            {/* PARTY & ACCOUNT INFO */}
          {/* PARTY & ACCOUNT INFO */}
<div style={sectionBox}>
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontWeight: '600', fontSize: '12px', color: '#334155' }}>
            {purchaseType === 'Supplier' ? 'Supplier' : 'Purchase from'}
        </span>
        {purchaseType === 'Supplier' ? (
            !selectedSupplier ? (
                <div style={{ position: 'relative' }}>
                    <input
                        style={inputStyle}
                        placeholder="Search supplier by name or phone..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    />
                    {searchTerm.trim() && (
                        <div style={dropdownStyle}>
                            {supplierResults.length === 0 ? (
                                <div style={itemStyle}>No supplier found</div>
                            ) : (
                                supplierResults.map(s => (
                                    <div
                                        key={s.id}
                                        style={itemStyle}
                                        onClick={() => { setSelectedSupplier(s); setSearchTerm(''); setSupplierResults([]); }}
                                    >
                                        {s.supplier_name} — {s.phone}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div style={confirmBox}>
                    <div><b>{selectedSupplier.supplier_name}</b> ({selectedSupplier.phone})</div>
                    <button onClick={() => setSelectedSupplier(null)} style={changeBtn}>Change</button>
                </div>
            )
        ) : (
            !selectedCustomer ? (
                <div style={{ position: 'relative', display: 'flex', gap: '5px' }}>
                    <input
                        style={{ ...inputStyle, flex: 1 }}
                        placeholder="Search Customer / Seller..."
                        value={customerSearch}
                        onChange={e => setCustomerSearch(e.target.value)}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    />
                    <button onClick={() => setShowCustomerModal(true)} style={addBtnStyle}>+ Add</button>
                    {customerSearch && (
                        <CustomerDropdownSearch query={customerSearch} onSelect={(cust) => { setSelectedCustomer(cust); setCustomerSearch(''); }} />
                    )}
                </div>
            ) : (
                <div style={confirmBox}>
                    <div><b>{selectedCustomer.full_name} ({selectedCustomer.phone})</b></div>
                    <button onClick={() => setSelectedCustomer(null)} style={changeBtn}>Change</button>
                </div>
            )
        )}
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center' }}>
        <span style={{ fontWeight: '600', fontSize: '12px', color: '#334155' }}>D.O # (Lot #)</span>
        <input
            style={inputStyle}
            placeholder="Lot / Delivery Order Number"
            value={invoiceNumber}
            onChange={e => setInvoiceNumber(e.target.value)}
            ref={el => formElementsRef.current[elIdx++] = el}
            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
        />
    </div>
</div>

            {/* CONFIGURATION & MATRIX SECTION */}
            <div style={sectionBox}>
                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '15px', marginBottom: '10px', alignItems: 'center' }}>
                    <label style={labelStyle}>Make</label>
                    <select
                        style={inputStyle}
                        value={lotDetails.model_name}
                        onChange={e => setLotDetails({ ...lotDetails, model_name: e.target.value, version: '', cc: '' })}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    >
                        <option value="">Select Make</option>
                        {uniqueModels.map((m, idx) => <option key={idx} value={m}>{m}</option>)}
                    </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '15px', marginBottom: '10px', alignItems: 'center' }}>
                    <label style={labelStyle}>Version</label>
                    <select
                        style={inputStyle}
                        value={lotDetails.version}
                        onChange={e => handleVersionChange(e.target.value)}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    >
                        <option value="">Select Version</option>
                        {filteredVersions.map((v, idx) => <option key={idx} value={v.version}>{v.version}</option>)}
                    </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '15px', marginBottom: '10px', alignItems: 'center' }}>
                    <label style={labelStyle}>CC</label>
                    <input
                        style={inputStyle}
                        type="number"
                        placeholder="Engine CC (e.g. 70, 125)"
                        value={lotDetails.cc}
                        onChange={e => setLotDetails({ ...lotDetails, cc: e.target.value })}
                        ref={el => formElementsRef.current[elIdx++] = el}
                        onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', gap: '15px', marginBottom: '15px', alignItems: 'center' }}>
                    <label style={labelStyle}>Unit Price</label>
                    {/* Unit Price is excluded from arrow navigation to prevent accidental changes */}
                    <input
                        style={inputStyle}
                        type="number"
                        placeholder="Single bike price"
                        value={lotDetails.unit_price}
                        onChange={e => setLotDetails({ ...lotDetails, unit_price: e.target.value })}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a' }}>Total Amount:</span>
                        <input style={{ ...inputStyle, background: '#f8fafc', fontWeight: '700', color: '#0f172a', textAlign: 'right' }} value={`Rs. ${isNaN(totalAmount) ? 0 : totalAmount}`} readOnly />
                    </div>
                </div>

                {/* MATRIX TABLE */}
                <div style={{ background: '#fff', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f1f5f9', fontSize: '12px', color: '#475569', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', width: '50px' }}>S.#</th>
                                <th style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Color</th>
                                <th style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Engine #</th>
                                <th style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>Chassis #</th>
                                {purchaseType === 'Supplier' && <th style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', width: '50px' }}>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {bikes.map((b, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f8fafc', verticalAlign: 'top' }}>
                                    <td style={{ padding: '8px 12px', fontWeight: '600', fontSize: '12px' }}>{i + 1}</td>
                                    <td style={{ padding: '6px 12px' }}>
                                        <select
                                            style={inputStyle}
                                            value={b.color_id}
                                            onChange={e => handleBikeDetailChange(i, 'color_id', e.target.value)}
                                            ref={el => formElementsRef.current[elIdx++] = el}
                                            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                                        >
                                            <option value="">Select Color</option>
                                            {colors.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </td>
                                    <td style={{ padding: '6px 12px' }}>
                                        <input
                                            style={b.engine_error ? errorInput : inputStyle}
                                            placeholder="Engine Number"
                                            value={b.engine_number}
                                            onChange={e => handleBikeDetailChange(i, 'engine_number', e.target.value)}
                                            onBlur={() => checkDuplicate(i, 'engine_number')}
                                            ref={el => formElementsRef.current[elIdx++] = el}
                                            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                                        />
                                        {b.engine_error && <div style={errorText}>{b.engine_error}</div>}
                                    </td>
                                    <td style={{ padding: '6px 12px' }}>
                                        <input
                                            style={b.chassis_error ? errorInput : inputStyle}
                                            placeholder="Chassis Number"
                                            value={b.chassis_number}
                                            onChange={e => handleBikeDetailChange(i, 'chassis_number', e.target.value)}
                                            onBlur={() => checkDuplicate(i, 'chassis_number')}
                                            ref={el => formElementsRef.current[elIdx++] = el}
                                            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                                        />
                                        {b.chassis_error && <div style={errorText}>{b.chassis_error}</div>}
                                    </td>
                                    {purchaseType === 'Supplier' && (
                                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                            {i !== 0 && <button onClick={() => removeBikeRow(i)} style={deleteBtn}>✕</button>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {purchaseType === 'Supplier' && (
                        <div style={{ padding: '8px 12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <button onClick={addBikeRow} style={addBtnStyle}>+ Add Another Bike Unit</button>
                        </div>
                    )}
                </div>
            </div>

            {/* PAYMENT BREAKDOWN SECTION */}
            <div style={sectionBox}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#334155' }}>Payment Breakdown (Cash / Bank)</h4>
                    <button onClick={addPaymentRow} style={addBtnStyle}>+ Add Payment Method</button>
                </div>
                {payments.map((p, i) => (
                    <div key={i} style={paymentRow}>
                        <select
                            style={inputStyle}
                            value={p.method}
                            onChange={e => updatePayment(i, { method: e.target.value, account_id: '' })}
                            ref={el => formElementsRef.current[elIdx++] = el}
                            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                        >
                            <option value="Cash">Cash</option>
                            <option value="Bank">Bank Transfer</option>
                        </select>
                        <select
                            style={inputStyle}
                            value={p.account_id}
                            onChange={e => updatePayment(i, { account_id: e.target.value })}
                            ref={el => formElementsRef.current[elIdx++] = el}
                            onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                        >
                            <option value="">Select Ledger Account ({p.method})</option>
                            {paymentAccounts
                                .filter(acc => acc.account_type === p.method)
                                .map(acc => <option key={acc.id} value={acc.id}>{acc.title}</option>)
                            }
                        </select>
                        <input
                            style={inputStyle}
                            type="number"
                            placeholder="Amount"
                            value={p.amount}
                            onChange={e => updatePayment(i, { amount: e.target.value })}
                        />
                        <button onClick={() => removePaymentRow(i)} style={deleteBtn}>✕</button>
                    </div>
                ))}
            </div>

            {/* REMARKS */}
            <div style={sectionBox}>
                <label style={miniLabel}>Remarks / Notes</label>
                <input
                    style={inputStyle}
                    placeholder="Optional transaction remarks"
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    ref={el => formElementsRef.current[elIdx++] = el}
                    onKeyDown={(e) => handleKeyDown(e, elIdx - 1)}
                />
            </div>

            {/* SUBMIT BUTTON */}
            <button
                style={mainSaveBtn}
                onClick={submitForm}
                disabled={loading}
                ref={el => formElementsRef.current[elIdx++] = el}
            >
                {loading ? 'Processing...' : 'Add Purchases'}
            </button>

            {showCustomerModal && (
                <AddCustomerModal onClose={() => setShowCustomerModal(false)} onRefresh={() => {}} />
            )}
        </div>
    );
};

const CustomerDropdownSearch = ({ query, onSelect }) => {
    const [results, setResults] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            API.get(`/v1/customers/search?q=${encodeURIComponent(query)}`)
                .then(res => { if (!cancelled) setResults(res.data.data || res.data || []); })
                .catch(() => { if (!cancelled) setResults([]); });
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query]);

    return (
        <div style={dropdownStyle}>
            {results.length === 0 ? <div style={itemStyle}>No customer found</div> :
                results.map(c => (
                    <div key={c.id} style={itemStyle} onClick={() => onSelect(c)}>
                        {c.full_name} — {c.phone} {c.cnic ? `(${c.cnic})` : ''}
                    </div>
                ))}
        </div>
    );
};

// --- ERP PROFESSIONAL STYLES ---
const containerStyle = { padding: '20px', background: '#f1f5f9', minHeight: '100vh', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", maxWidth: '1000px', margin: '0 auto' };
const headerCard = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', background: '#ffffff', padding: '12px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const tabContainer = { display: 'flex', gap: '6px', background: '#f8fafc', padding: '4px', borderRadius: '6px', border: '1px solid #e2e8f0' };
const tabStyle = { padding: '6px 14px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b' };
const activeTab = { ...tabStyle, background: '#0f172a', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' };

const metaGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px', background: '#ffffff', padding: '12px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' };
const metaFieldBox = { display: 'flex', alignItems: 'center', gap: '10px' };
const metaLabel = { fontWeight: '600', fontSize: '12px', color: '#475569', width: '60px' };

const sectionBox = { background: '#ffffff', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' };
const labelStyle = { fontWeight: '600', fontSize: '12px', color: '#334155' };
const miniLabel = { fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' };

const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const disabledInput = { ...inputStyle, background: '#f8fafc', color: '#64748b' };
const errorInput = { ...inputStyle, border: '1px solid #ef4444', background: '#fef2f2' };
const errorText = { color: '#dc2626', fontSize: '11px', marginTop: '3px' };

const paymentRow = { display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '10px', marginTop: '8px', background: '#f8fafc', padding: '8px', borderRadius: '6px', alignItems: 'center', border: '1px solid #e2e8f0' };
const addBtnStyle = { padding: '5px 12px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' };
const deleteBtn = { background: '#fee2e2', color: '#ef4444', border: 'none', width: '28px', height: '28px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const mainSaveBtn = { width: '100%', padding: '12px', background: '#0f172a', color: '#fff', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', border: 'none', marginTop: '5px', boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.2)' };

const confirmBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', padding: '6px 10px', borderRadius: '6px', border: '1px solid #dcfce7', fontSize: '12px' };
const changeBtn = { background: '#fff', color: '#dc2626', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', zIndex: 100, border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' };
const itemStyle = { padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f1f5f9' };

export default AddPurchaseForm;
